import React, { useState, useEffect, useContext } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import axios from 'axios';
import { CustomerContext } from '../../../contexts/CustomerContext';
import CartModel from '../../../models/Cart';
import OrderPackage from '../../../models/OrderPackage';
import { Coupon } from '../../../models/_models';
import styles from './StripeTestCheckout.module.scss';
import { routes } from '../../../routes';

const TestReview = () => {
  const history = useHistory();
  const location = useLocation();
  const { customer, setCustomer } = useContext(CustomerContext);
  const [cart, setCart] = useState(null);
  const [couponCode, setCouponCode] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState('');

  const fetchCart = (cust) => {
    if (!cust?.cart) return;
    CartModel
      .where({ for_email: [cust.cart.id, cust.email] })
      .includes({ orderPackages: { student: {}, apiOption: {}, package: {}, sheets: { pose: {}, background: {} } }, coupon: {} })
      .first()
      .then(r => setCart(r.data));
  };

  useEffect(() => {
    fetchCart(customer);
  }, [customer]);

  // Redirect to home if no cart or empty cart
  useEffect(() => {
    if (!customer?.cart) {
      history.push('/');
    }
  }, [customer?.cart, history]);

  useEffect(() => {
    if (cart && (cart.orderPackages || []).length === 0) {
      history.push('/');
    }
  }, [cart, history]);

  useEffect(() => {
    const needsRefresh = localStorage.getItem('needsCartRefresh');
    const lastEditTimestamp = localStorage.getItem('lastEditTimestamp');
    const urlParams = new URLSearchParams(location.search);
    const refreshParam = urlParams.get('refresh');

    if (needsRefresh === 'true' || lastEditTimestamp || refreshParam) {
      localStorage.removeItem('needsCartRefresh');
      localStorage.removeItem('lastEditTimestamp');
      if (refreshParam) {
        window.history.replaceState({}, '', location.pathname);
      }
      fetchCart(customer);
    }
  }, [location.search, location.pathname]);

  const handleEditItem = (op) => {
    setCustomer({ ...customer, currentOrderPackage: op, currentStudent: op.student });
    localStorage.setItem('reviewReturnPath', routes.testReview());
    history.push(routes.multiPoseEdit(op.id));
  };

  const handleApplyCoupon = async (e) => {
    e.preventDefault();
    if (!couponCode || !cart) return;
    setCouponLoading(true);
    setCouponError('');

    const code = couponCode.toUpperCase();

    try {
      const res = await axios({
        baseURL: '',
        method: 'get',
        url: `/coupons/for_cart?cart_id=${cart.id}&code=${code}`,
      });

      if (res.data.message) {
        setCouponError(res.data.message);
        setCouponLoading(false);
        return;
      }

      const couponRes = await Coupon.where({ code }).first();
      const coupon = couponRes.data;

      const dbCart = await CartModel
        .where({ for_email: [cart.id, customer.email], purchased: false })
        .first()
        .then(r => r.data);

      dbCart.email = customer.email;
      dbCart.coupon = coupon;
      dbCart.couponId = coupon.id;
      await dbCart.save();

      fetchCart(customer);
      setCouponCode('');
    } catch (err) {
      setCouponError('Failed to apply coupon. Please try again.');
    }

    setCouponLoading(false);
  };

  const handleRemoveItem = async (orderPackageId) => {
    try {
      const op = new OrderPackage({ id: orderPackageId, cartId: cart.id });
      op.isPersisted = true;
      await op.destroy();
      fetchCart(customer);
    } catch (err) {
      console.error('Failed to remove item:', err);
    }
  };

  const handleRemoveCoupon = async () => {
    if (!cart) return;
    try {
      const dbCart = await CartModel
        .where({ for_email: [cart.id, customer.email], purchased: false })
        .first()
        .then(r => r.data);

      dbCart.email = customer.email;
      dbCart.couponId = null;
      await dbCart.save();

      fetchCart(customer);
    } catch (err) {
      console.error('Failed to remove coupon:', err);
    }
  };

  if (!customer?.cart || !cart) {
    return (
      <div className={styles.reviewContainer}>
        <div className={styles.loadingState}>
          <div className={styles.spinner}></div>
          <p>Loading cart...</p>
        </div>
      </div>
    );
  }

  const orderPackages = cart.orderPackages || [];
  const hasCoupon = cart.couponValue > 0;
  const total = (cart.apiTotal || 0).toFixed(2);

  return (
    <div className={styles.reviewContainer}>
      <div className={styles.reviewLayout}>
        <div className={styles.reviewItemsSection}>
          <div className={styles.card}>
            <h1 className={styles.reviewTitle}>
              Cart ({orderPackages.length} {orderPackages.length === 1 ? 'item' : 'items'})
            </h1>

            <div className={styles.orderItems}>
              {orderPackages.map((op, index) => {
                const student = op.student;
                const option = op.apiOption || op.option?.();
                const packageName = option?.name || 'Package';
                const firstSheet = op.sheets?.[0];
                const imageUrl = firstSheet?.pose?.watermarkUrl;
                const backgroundUrl = firstSheet?.background?.imageUrl;
                const price = op.price || 0;

                return (
                  <div key={op.id || index} className={styles.orderItem}>
                    <div className={styles.studentImageWrapper}>
                      {backgroundUrl && (
                        <img src={backgroundUrl} alt="" className={styles.studentImageBackground} />
                      )}
                      {imageUrl && (
                        <img
                          src={imageUrl}
                          alt={student?.firstName || 'Student'}
                          className={styles.studentImagePose}
                        />
                      )}
                    </div>
                    <div className={styles.orderItemDetails}>
                      <div className={styles.packageName}>{packageName}</div>
                      {op.pose && (
                        <div className={styles.studentName}>{op.pose}</div>
                      )}
                      {student && (
                        <div className={styles.studentName}>
                          {student.firstName} {student.lastName}
                        </div>
                      )}
                      <div className={styles.packagePrice}>${price.toFixed(2)}</div>
                    </div>
                    <div className={styles.itemActions}>
                      <button
                        type="button"
                        onClick={() => handleEditItem(op)}
                        className={styles.editItemButton}
                        aria-label="Edit item"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
                          <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
                        </svg>
                      </button>
                      <button
                        type="button"
                        onClick={() => handleRemoveItem(op.id)}
                        className={styles.removeItemButton}
                        aria-label="Remove item"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="3 6 5 6 21 6" />
                          <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" />
                          <path d="M10 11v6" />
                          <path d="M14 11v6" />
                          <path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2" />
                        </svg>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className={styles.couponSection}>
              {hasCoupon ? (
                <div className={styles.couponApplied}>
                  <span>{cart.coupon?.code} &mdash; ${(cart.couponValue || 0).toFixed(2)} off</span>
                  <button
                    type="button"
                    onClick={handleRemoveCoupon}
                    className={styles.removeCouponLink}
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <form onSubmit={handleApplyCoupon}>
                  <p className={styles.couponTitle}>Have a coupon code?</p>
                  <div className={styles.couponInputRow}>
                    <input
                      type="text"
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value)}
                      placeholder="Enter coupon code"
                      className={styles.input}
                    />
                    <button type="submit" disabled={couponLoading || !couponCode}>
                      {couponLoading ? 'Applying...' : 'Apply'}
                    </button>
                  </div>
                  {couponError && <p className={styles.couponError}>{couponError}</p>}
                </form>
              )}
            </div>

            <div className={styles.reviewActions}>
              <button
                type="button"
                onClick={() => history.push('/')}
                className={styles.continueShoppingLink}
              >
                ← Continue shopping
              </button>
              <button
                type="button"
                onClick={() => history.push(routes.testCheckout())}
                className={styles.checkoutButton}
              >
                Checkout →
              </button>
            </div>
          </div>
        </div>

        <div className={styles.reviewSummarySection}>
          <div className={styles.orderSummary}>
            <h2 className={styles.orderSummaryTitle}>Order Summary</h2>
            <div className={styles.orderTotals}>
              <div className={styles.orderTotalRow}>
                <span>Subtotal</span>
                <span>${(cart.subtotal || 0).toFixed(2)}</span>
              </div>
              {hasCoupon && (
                <div className={styles.orderTotalRow}>
                  <span>Discount ({cart.coupon?.code})</span>
                  <span className={styles.discountAmount}>-${(cart.couponValue || 0).toFixed(2)}</span>
                </div>
              )}
              <div className={styles.orderTotalRow}>
                <span>Shipping</span>
                <span>{cart.shipping > 0 ? `$${cart.shipping.toFixed(2)}` : 'FREE'}</span>
              </div>
              <div className={styles.orderTotalRow}>
                <span>Estimated tax</span>
                <span>${(cart.apiTax || 0).toFixed(2)}</span>
              </div>
              <div className={`${styles.orderTotalRow} ${styles.orderTotalFinal}`}>
                <span>Total</span>
                <span>${total}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.mobileStickyBar}>
        <span className={styles.mobileStickyTotal}>Total: ${total}</span>
        <button
          type="button"
          onClick={() => history.push(routes.testCheckout())}
          className={styles.mobileStickyCheckout}
        >
          Checkout →
        </button>
      </div>
    </div>
  );
};

export default TestReview;
